/**
 * webFormat — presentational formatters for the CompassCare Web facelift's
 * dense command-center screens (tables, cards, stat tiles).
 *
 * Presentation-only: this module shapes already-resolved values for display
 * (for TZ-aware calendar rendering use `activityTimezone`). Note that
 * `formatRelativeTime` parses date strings with JS `Date` semantics, so an
 * offset-less string (e.g. "2026-07-07T10:00:00") is read in the runtime's
 * local timezone — pass an ISO string with an explicit offset, an epoch-ms
 * number, or a Date for a stable result. Every formatter is fail-safe — given a
 * missing or malformed value it
 * returns a stable placeholder ("—", or "" for relative time) rather than
 * throwing or emitting "NaN"/"undefined", so a single bad row never breaks a
 * table render.
 *
 * GMA-1616 — CompassCare Web UI/UE Facelift (Foundation).
 */

const PLACEHOLDER = "—";

/**
 * Coerce an ISO string / epoch-ms number / Date to epoch milliseconds, or null
 * when not parseable. Shared by the web models (alert age, staleness) so the
 * date-coercion rule can't drift between them.
 * @param {*} value
 * @returns {number|null}
 */
const toEpochMs = (value) => {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
};

/** Coerce a value to a finite number, or null (accepts finite numeric strings). */
const toFiniteNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/**
 * Group-separated number for dense tables (e.g. 12480 -> "12,480"). When
 * `decimals` is given the value is rounded to that many fixed places
 * (98.6, {decimals:1} -> "98.6"). Missing / non-numeric input -> `fallback`.
 */
const formatNumber = (value, options = {}) => {
  // `= {}` only covers `undefined`; a caller plumbing options from data/config
  // could pass `null`, which would throw on destructure — normalize first so
  // the no-throw contract holds.
  const { decimals, fallback = PLACEHOLDER } = options ?? {};
  const n = toFiniteNumber(value);
  if (n === null) return fallback;
  // Fail-safe: Intl fraction-digit counts must be integers in 0-20, else
  // toLocaleString throws RangeError. Only apply fixed decimals when `decimals`
  // is valid; any other value (negative, non-integer, >20, NaN) degrades to
  // default grouping rather than throwing (the module's no-throw contract).
  const useDecimals =
    typeof decimals === "number" &&
    Number.isInteger(decimals) &&
    decimals >= 0 &&
    decimals <= 20;
  const opts = useDecimals
    ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
    : undefined;
  return n.toLocaleString("en-US", opts);
};

/**
 * A value + unit for stat tiles / vitals (72, "bpm" -> "72 bpm"). Delegates to
 * `formatNumber`, so grouping, fixed decimals and the fallback all apply. The
 * unit is dropped from the placeholder so a missing reading reads as "—", never
 * "— bpm".
 */
const formatMeasurement = (value, unit = "", options = {}) => {
  const opts = options ?? {}; // tolerate an explicit null options (no-throw)
  const fallback = opts.fallback ?? PLACEHOLDER;
  // Decide "missing" from the INPUT, not by comparing the formatted text to
  // fallback: a caller whose custom fallback equals a valid formatted value
  // (e.g. fallback "0" with value 0) must still get its unit.
  if (toFiniteNumber(value) === null) return fallback;
  const text = formatNumber(value, { ...opts, fallback });
  return unit ? `${text} ${unit}` : text;
};

/**
 * Human duration from a count of seconds, largest-two-units, no zero-padding:
 * 45 -> "45s", 90 -> "1m 30s", 5025 -> "1h 23m", 3600 -> "1h". Sub-minute shows
 * seconds; zero -> "0s". Negative / missing / non-numeric -> `fallback`.
 */
const formatDuration = (seconds, options = {}) => {
  const { fallback = PLACEHOLDER } = options ?? {}; // tolerate explicit null
  const total = toFiniteNumber(seconds);
  if (total === null || total < 0) return fallback;
  const s = Math.floor(total);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

/**
 * "just now" / "5m ago" / "2h ago" / "3d ago" from an ISO string, epoch-ms, or
 * Date. Mirrors the inline helper in WatchVitalsSection (same thresholds and
 * rounding) so that view can later import this shared version. Future
 * timestamps clamp to "just now"; missing / invalid input -> "".
 */
const formatRelativeTime = (value, now = Date.now()) => {
  if (value === null || value === undefined || value === "") return "";
  // A malformed `now` (e.g. a string) would make (now - then) NaN and emit
  // "NaNd ago"; guard it so the module's no-"NaN" contract holds.
  if (typeof now !== "number" || !Number.isFinite(now)) return "";
  // Only Date, epoch-ms number, or a date string are valid inputs. Reject other
  // types (notably boolean, which `new Date()` would coerce to an epoch date and
  // render as a bogus "Xd ago") so the "invalid -> ''" contract holds.
  let then;
  if (value instanceof Date) then = value.getTime();
  else if (typeof value === "number") then = Number.isFinite(value) ? value : NaN;
  else if (typeof value === "string") then = new Date(value).getTime();
  else return "";
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
};

export {
  PLACEHOLDER,
  toEpochMs,
  formatNumber,
  formatMeasurement,
  formatDuration,
  formatRelativeTime,
};
